import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Shield, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { usePricing } from '@/hooks/usePricing';
import axios from 'axios';
import SEO from '@/components/SEO';
import { trackInitiateCheckout } from '@/utils/metaPixel';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import EmbeddedPaymentForm from '@/components/EmbeddedPaymentForm';
import { useAuth } from '@/context/AuthContext';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png";
// Hardcoded PayPal Client ID for production (environment variable has incorrect value)
const PAYPAL_CLIENT_ID = "AQDiyFNI-3Atg-zOg6GPPCiii7G8gaM5nWLKnXmoJ3tYUCWZrXrL9K-dnT9l_3h8BT6Onzs7q08oJpZ1";

// Initialize Stripe.js once — outside the component to avoid recreating on every render
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Payment method icons (SVG data URLs)
const PaymentIcons = () => (
  <div className="flex items-center justify-center gap-3 flex-wrap">
    {/* Visa */}
    <div className="w-10 h-6 bg-white rounded border border-[#EAD8C2] flex items-center justify-center">
      <svg viewBox="0 0 48 32" className="w-7 h-4">
        <rect fill="#1A1F71" width="48" height="32" rx="4"/>
        <text x="24" y="20" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">VISA</text>
      </svg>
    </div>
    {/* Mastercard */}
    <div className="w-10 h-6 bg-white rounded border border-[#EAD8C2] flex items-center justify-center">
      <svg viewBox="0 0 48 32" className="w-7 h-4">
        <circle cx="18" cy="16" r="10" fill="#EB001B"/>
        <circle cx="30" cy="16" r="10" fill="#F79E1B"/>
        <path d="M24 8.5a10 10 0 000 15" fill="#FF5F00"/>
      </svg>
    </div>
    {/* American Express */}
    <div className="w-10 h-6 bg-[#006FCF] rounded border border-[#EAD8C2] flex items-center justify-center">
      <span className="text-white text-[7px] font-bold">AMEX</span>
    </div>
    {/* Affirm */}
    <div className="w-10 h-6 bg-[#0FA0EA] rounded border border-[#EAD8C2] flex items-center justify-center">
      <span className="text-white text-[6px] font-bold lowercase tracking-tight">affirm</span>
    </div>
    {/* Klarna */}
    <div className="w-10 h-6 bg-[#FFB3C7] rounded border border-[#EAD8C2] flex items-center justify-center">
      <span className="text-black text-[6px] font-bold">Klarna</span>
    </div>
  </div>
);

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paypalLoading, setPaypalLoading] = useState(false);
  const pricing = usePricing();
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [error, setError] = useState(
    searchParams.get('payment_failed') === '1'
      ? "That payment method didn't go through (Klarna, Affirm and Amazon Pay make their own approval decision). Nothing was charged — please try a card or another option below."
      : null
  );
  const [package_, setPackage] = useState(null);

  // Embedded Payment Element state
  const [clientSecret, setClientSecret] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);

  // Check for session_id in URL (returning from Stripe — legacy redirect flow)
  const sessionId = searchParams.get('session_id');

  // Check for PayPal return parameters
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');

  // Check if this is an upgrade checkout (for trial users)
  const isUpgrade = searchParams.get('upgrade') === 'true';

  // Check if this is the $9 7-Day Power Start trial
  const isTrial = searchParams.get('trial') === 'true';

  // NEW: Branded Glow Up Gap trial flow → $9 today + 7-day trial + $37/mo recurring subscription.
  // Routed here from /glow-up-gap results page with email + first_name + report_id in the URL.
  const planParam = searchParams.get('plan');
  const isGlowUpTrial = planParam === 'glow-up-trial';
  // Direct monthly membership → $37 today + $37/mo recurring (no trial).
  // Replaces the retired $97 Lifetime flow as the primary public checkout.
  // DEFAULT: If no flow param at all (no plan, no upgrade, no trial, no Stripe/PayPal return),
  // treat the visit as monthly_direct. This catches every legacy `/checkout` link still in the wild —
  // old emails, social posts, the /signup "Purchase access" link, etc. — and prevents users from
  // landing on the dead $97 Lifetime checkout.
  const isLegacyReturn = Boolean(searchParams.get('session_id') || searchParams.get('paymentId') || searchParams.get('PayerID'));
  const hasExplicitFlow = Boolean(planParam) || isUpgrade || isTrial || isLegacyReturn;
  // Oct 2026: default public checkout is one-time Lifetime Access again. Monthly plan retired.
  const isMonthlyDirect = false;
  const isLifetime = planParam === 'lifetime' || planParam === 'monthly' || !hasExplicitFlow;
  const glowUpEmail = searchParams.get('email') || '';
  const glowUpFirstName = searchParams.get('first_name') || '';
  const glowUpReportId = searchParams.get('report_id') || '';

  // Inline email + first_name collection for non-logged-in users hitting /checkout?plan=monthly.
  // We need these before we can create the PaymentIntent (Stripe Customer is created up front).
  const [monthlyEmail, setMonthlyEmail] = useState(user?.email || glowUpEmail || '');
  const [monthlyFirstName, setMonthlyFirstName] = useState(user?.first_name || glowUpFirstName || '');
  const [monthlyDetailsLocked, setMonthlyDetailsLocked] = useState(
    Boolean((user?.email || glowUpEmail) && (user?.first_name || glowUpFirstName))
  );

  // If the auth context resolves after mount (typical for logged-in users), backfill + auto-lock so we
  // don't show the email/first_name gate to someone who already has those values.
  useEffect(() => {
    if (!isMonthlyDirect) return;
    if (user?.email && !monthlyEmail) setMonthlyEmail(user.email);
    if (user?.first_name && !monthlyFirstName) setMonthlyFirstName(user.first_name);
    if (user?.email && user?.first_name) setMonthlyDetailsLocked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.first_name, isMonthlyDirect]);

  // Resolve access_type for the embedded PaymentIntent
  const accessType = isMonthlyDirect
    ? 'monthly_direct'
    : isGlowUpTrial
      ? 'glow_up_trial'
      : isTrial
        ? 'trial'
        : isUpgrade
          ? 'upgrade'
          : 'full';

  // If upgrade checkout but user isn't logged in, redirect to login first
  useEffect(() => {
    if (isUpgrade && !sessionId && !paymentId) {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login?redirect=' + encodeURIComponent('/checkout?upgrade=true'));
      }
    }
  }, [isUpgrade, sessionId, paymentId, navigate]);

  // Fetch package info on mount
  useEffect(() => {
    const fetchPackage = async () => {
      try {
        // Trial users use a fixed package (no auth needed); upgrade users use the upgrade endpoint
        if (isLifetime) {
          setPackage({
            name: 'Dea Lifetime Access',
            price: pricing.lifetime_price,
            features: [
              'Full access to Dea, your AI Ambition Coach, available 24/7',
              'Your BECOMING HER Dashboard, Habit Tracker, Daily Journal and Weekly Check-in',
              'Your personalized 90-day plan with daily clarity and direction',
              'One payment. No subscription. Yours for good.',
            ],
          });
          return;
        }
        if (isGlowUpTrial) {
          setPackage({
            name: 'Dea — 7-Day Trial',
            price: 9,
            features: [
              'Your personalized 90-day roadmap — built around your Glow Up Gap in your first session',
              'Daily check-ins with Dea that adjust to your energy, your season, and your real life',
              'Habit tracker, daily journal, and milestone system that keep your streak protected',
              'A coach who knows exactly where you are in your journey — every single day',
            ],
          });
          return;
        }
        if (isTrial) {
          setPackage({
            name: 'Dea — 7-Day Trial',
            price: 9,
            features: [
              'Full access to Dea — your AI Ambition Coach',
              'Complete Goal Clarity Sequence on Day 1',
              'Your personalized 90-day Glow Up roadmap',
              'Daily check-ins on habits, mindset & goals',
              `No automatic charge — your $9 applies toward lifetime access ($${pricing.upgrade_price} to unlock)`
            ]
          });
          return;
        }
        const endpoint = isUpgrade ? `${API}/payment/package/upgrade` : `${API}/payment/package`;
        const response = await axios.get(endpoint);
        setPackage({ ...response.data, name: 'Dea Lifetime Access — Trial Upgrade', price: pricing.upgrade_price });
      } catch (err) {
        console.error('Failed to fetch package:', err);
        // Fallback for upgrade if endpoint doesn't exist yet
        if (isUpgrade) {
          setPackage({
            name: 'Dea Lifetime Access — Trial Upgrade',
            price: pricing.upgrade_price,
            features: [
              "Upgrade from your 7-Day Power Start",
              "Unlock your complete 90-day transformation",
              "Full access to Dea, your AI business coach",
              "12-week milestone tracking",
              "Lifetime access - no recurring fees"
            ]
          });
        }
      }
    };
    fetchPackage();
  }, [isUpgrade, isTrial, isGlowUpTrial, isLifetime, pricing.lifetime_price, pricing.upgrade_price]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  // Create PaymentIntent for embedded Elements as soon as the access_type is known.
  // Skip if returning from a Stripe/PayPal redirect (we're in status-check mode).
  useEffect(() => {
    if (sessionId || paymentId) return; // Returning from external redirect — skip
    if (clientSecret) return; // Already created
    if (isUpgrade) {
      // Upgrade needs auth token — bail out if not logged in (effect upstream handles redirect to /login)
      const token = localStorage.getItem('token');
      if (!token) return;
    }
    if (isMonthlyDirect) {
      // Need first_name + email before creating the Stripe Customer / PaymentIntent.
      // Logged-in users have these from their account (auto-locked below); otherwise we wait for the inline gate form.
      if (!monthlyEmail || !monthlyFirstName || !monthlyDetailsLocked) return;
    }
    if (isGlowUpTrial) {
      // The Glow Up Gap funnel passes email/first_name in the URL. If not present
      // (e.g. user came from /powerstart), we require the inline gate form first.
      const haveTrialContact = (glowUpEmail && glowUpFirstName) || (monthlyEmail && monthlyFirstName && monthlyDetailsLocked);
      if (!haveTrialContact) return;
    }

    const createIntent = async () => {
      setIntentLoading(true);
      // Preserve the "payment_failed" banner from a declined Klarna/Affirm/Amazon Pay redirect
      if (searchParams.get('payment_failed') !== '1') setError(null);
      try {
        const token = localStorage.getItem('token');
        const headers = isUpgrade && token ? { Authorization: `Bearer ${token}` } : {};
        const payload = { access_type: accessType, origin_url: window.location.origin };
        if (isGlowUpTrial) {
          // Prefer URL params (funnel flow); fall back to inline-gate values (from /powerstart).
          payload.email = (glowUpEmail || monthlyEmail || '').trim().toLowerCase();
          payload.first_name = (glowUpFirstName || monthlyFirstName || '').trim();
          payload.report_id = glowUpReportId;
        }
        if (isMonthlyDirect) {
          payload.email = monthlyEmail.trim().toLowerCase();
          payload.first_name = monthlyFirstName.trim();
        }
        const response = await axios.post(
          `${API}/payment/intent/create`,
          payload,
          { headers }
        );
        setClientSecret(response.data.client_secret);

        // Track InitiateCheckout once PaymentIntent is ready (matches old behavior timing)
        trackInitiateCheckout({
          value: response.data.amount,
          currency: 'USD',
          content_name: isMonthlyDirect
            ? 'Dea Lifetime Access'
            : isGlowUpTrial
              ? 'Dea 7-Day Trial'
              : isTrial
                ? 'Dea 7-Day Trial'
                : isUpgrade
                  ? 'Dea Lifetime Upgrade'
                  : 'Dea Lifetime Access',
        });
      } catch (err) {
        console.error('PaymentIntent creation error:', err);
        if (isUpgrade && (err.response?.status === 401 || err.response?.status === 403)) {
          toast.error('Please log in to complete your upgrade.');
          navigate('/login?redirect=' + encodeURIComponent('/checkout?upgrade=true'));
          return;
        }
        setError(err.response?.data?.detail || 'Could not initialize secure checkout. Please refresh.');
      } finally {
        setIntentLoading(false);
      }
    };
    createIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessType, isUpgrade, isTrial, isGlowUpTrial, isMonthlyDirect, sessionId, paymentId, monthlyDetailsLocked]);

  // Handle PayPal return
  useEffect(() => {
    if (paymentId && payerId) {
      capturePayPalPayment(paymentId, payerId);
    }
  }, [paymentId, payerId]);
  
  const capturePayPalPayment = async (paymentId, payerId) => {
    setCheckingStatus(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/paypal/capture?paymentId=${paymentId}&PayerID=${payerId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        // Upgrade flow: existing user upgraded → go straight to chat
        if (data.access_type === 'upgrade' || (data.redirect_url && data.redirect_url.startsWith('/chat'))) {
          toast.success('Upgrade complete! Welcome to full access.');
          setTimeout(() => {
            navigate(data.redirect_url || '/chat');
          }, 1500);
          return;
        }

        // New customer flow: invitation code → signup
        if (data.invitation_code) {
          toast.success('Payment successful! Redirecting to signup...');
          setTimeout(() => {
            navigate(`/signup?code=${data.invitation_code}`);
          }, 1500);
          return;
        }
      }

      setError('Payment could not be completed. Please contact support.');
      setCheckingStatus(false);
    } catch (err) {
      console.error('PayPal capture error:', err);
      setError(err.response?.data?.detail || 'Failed to complete PayPal payment.');
      setCheckingStatus(false);
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation or contact support.');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/payment/status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid' && data.invitation_code) {
        toast.success('Payment successful! Redirecting to signup...');
        setTimeout(() => {
          navigate(`/signup?code=${data.invitation_code}`);
        }, 1500);
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setCheckingStatus(false);
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setError('Error checking payment status. Please try again.');
        setCheckingStatus(false);
      }
    }
  };

  // Legacy handleStripeCheckout removed — replaced by embedded Stripe Payment Element
  // (see <EmbeddedPaymentForm /> below). PaymentIntent is created on mount via
  // /api/payment/intent/create and confirmed on-page (no redirect for cards).

  // PayPal button handlers
  const createPayPalOrder = async () => {
    setPaypalLoading(true);
    setError(null);
    
    // Track InitiateCheckout event
    trackInitiateCheckout({
      value: package_?.price || (isUpgrade ? pricing.upgrade_price : pricing.lifetime_price),
      currency: 'USD',
      content_name: isUpgrade ? 'Dea Lifetime Upgrade (PayPal)' : 'Dea Lifetime Access (PayPal)'
    });
    
    try {
      // For upgrades, the backend requires the user's auth token
      const token = localStorage.getItem('token');
      if (isUpgrade && !token) {
        toast.error('Please log in to complete your upgrade.');
        navigate('/login?redirect=' + encodeURIComponent('/checkout?upgrade=true'));
        return;
      }

      const successPath = isUpgrade ? '/checkout/paypal-success?upgrade=true' : '/checkout/paypal-success';
      const cancelPath = isUpgrade ? '/checkout?upgrade=true' : '/checkout?plan=monthly';

      const response = await axios.post(
        `${API}/paypal/create-order`,
        {
          return_url: `${window.location.origin}${successPath}`,
          cancel_url: `${window.location.origin}${cancelPath}`,
          is_upgrade: isUpgrade
        },
        isUpgrade && token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      
      // Redirect to PayPal approval URL
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      }
      
      return response.data.order_id;
    } catch (err) {
      console.error('PayPal order creation error:', err);
      if (isUpgrade && (err.response?.status === 401 || err.response?.status === 403)) {
        toast.error('Please log in to complete your upgrade.');
        navigate('/login?redirect=' + encodeURIComponent('/checkout?upgrade=true'));
        return;
      }
      setError(err.response?.data?.detail || 'Failed to create PayPal order.');
      setPaypalLoading(false);
      throw err;
    }
  };

  // Show loading state while checking payment status
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#FFFCF7] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="h-12 w-12 text-[#87A6A7] animate-spin mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-[#726158] mb-2">Processing Your Payment</h2>
          <p className="text-[#AA9283]">Please wait while we verify your payment...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={isGlowUpTrial || isTrial ? "Start Your 7-Day Trial with Dea — $9" : isUpgrade ? `Unlock Lifetime Access — $${pricing.upgrade_price}` : `Dea Lifetime Access — $${pricing.lifetime_price} one-time`}
        description={isMonthlyDirect
          ? "Get lifetime access to Dea, your AI Ambition Coach. One payment, no subscription."
          : isGlowUpTrial
          ? "Start your 7-day trial with Dea for $9. No automatic charges — unlock lifetime access only if you choose to."
          : isTrial 
          ? "Start your 7-Day Power Start with Dea for $9. Full access for 7 days. No auto-renewal."
          : "Get lifetime access to Dea, your AI Ambition Coach. One payment, no subscription."
        }
      />
      <div className="min-h-screen bg-[#FFFCF7]">
      {/* Header */}
      <header className="bg-[#FFFCF7]/90 backdrop-blur-lg border-b border-[#EAD8C2] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft className="h-5 w-5 text-[#AA9283]" />
            <img src={LOGO_URL} alt="Dea" className="h-8 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-2 text-[#AA9283]">
            <Shield className="h-4 w-4" />
            <span className="text-sm">Secure Checkout</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Product Info - Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3 space-y-6"
          >
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl text-[#726158] mb-2">
                {isMonthlyDirect
                  ? 'Dea Lifetime Access'
                  : isGlowUpTrial
                  ? 'Complete Your Purchase'
                  : isTrial ? 'Start Your 7-Day Trial'
                  : isUpgrade ? 'Complete Your Upgrade'
                  : 'Complete Your Purchase'}
              </h1>
              <p className="text-[#AA9283] text-sm sm:text-base">
                {isMonthlyDirect
                  ? "Get full access to Dea today. $37/month, cancel anytime — no contracts, no hoops."
                  : isGlowUpTrial
                  ? "You're one step away from meeting Dea. $9 today, no automatic charges after your 7 days."
                  : isTrial
                    ? "You're one step away from meeting Dea. Full access starts the moment you check out."
                    : isUpgrade
                      ? "Unlock your full 90-day transformation journey and continue with Dea."
                      : "You're one step away from transforming your business and life."}
              </p>
            </div>

            {/* Trial Banner */}
            {isTrial && (
              <div className="bg-gradient-to-r from-[#CAAB84]/20 to-[#87A6A7]/20 rounded-xl p-4 border border-[#CAAB84]/30">
                <p className="text-sm text-[#726158]">
                  <span className="font-semibold">7 days of full access · No auto-renewal.</span> Your $9 applies toward lifetime access ($97) if you choose to keep going.
                </p>
              </div>
            )}

            {/* Upgrade Banner for trial users */}
            {isUpgrade && (
              <div className="bg-gradient-to-r from-[#CAAB84]/20 to-[#87A6A7]/20 rounded-xl p-4 border border-[#CAAB84]/30">
                <p className="text-sm text-[#726158]">
                  <span className="font-semibold">You're upgrading from your 7-Day Power Start!</span> Your progress and conversations with Dea are safe. Continue right where you left off.
                </p>
              </div>
            )}

            {/* Product Card */}
            <div className="glass-card rounded-2xl p-5 sm:p-6 border-2 border-[#87A6A7]/30">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="font-serif text-lg sm:text-xl text-[#726158]">
                    {package_?.name || (isMonthlyDirect ? 'Dea Lifetime Access' : isGlowUpTrial ? 'Dea — 7-Day Trial' : isTrial ? 'Dea — 7-Day Trial' : isUpgrade ? 'Dea Lifetime Access — Trial Upgrade' : 'Dea Lifetime Access')}
                  </h2>
                  <p className="text-[#AA9283] text-xs sm:text-sm mt-1">
                    {isMonthlyDirect
                      ? 'Full access to Dea · One-time payment'
                      : isGlowUpTrial
                      ? 'Your personalized 90-day glow up system starts today'
                      : isTrial ? '7 days of full access · No subscription' : 'One-time payment, no recurring fees'}
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-serif text-2xl sm:text-3xl text-[#87A6A7]">${package_?.price || (isTrial || isGlowUpTrial ? 9 : isUpgrade ? pricing.upgrade_price : pricing.lifetime_price)}</span>
                  <p className="text-[#B1897C] text-xs">
                    {isMonthlyDirect ? '/month' : isGlowUpTrial || isTrial ? 'one-time · no automatic charge' : 'USD'}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {(package_?.features || (isMonthlyDirect ? [
                  "Full access to Dea — your AI Ambition Coach",
                  "Your personalized 90-day glow up roadmap",
                  "Daily check-ins, habit tracking, and goal momentum",
                  "One payment. No subscription. Yours for good.",
                ] : isGlowUpTrial ? [
                  "Your personalized 90-day roadmap — built around your Glow Up Gap in your first session",
                  "Daily check-ins with Dea that adjust to your energy, your season, and your real life",
                  "Habit tracker, daily journal, and milestone system that keep your streak protected",
                  "A coach who knows exactly where you are in your journey — every single day",
                ] : isTrial ? [
                  "Full access to Dea — your AI Ambition Coach",
                  "Complete Goal Clarity Sequence on Day 1",
                  "Your personalized 90-day Glow Up roadmap",
                  "Daily check-ins on habits, mindset & goals",
                  "Your $9 applies toward lifetime access ($97)"
                ] : isUpgrade ? [
                  "Upgrade from your 7-Day Power Start",
                  "Unlock your complete 90-day transformation",
                  "Full access to Dea, your AI business coach",
                  "12-week milestone tracking",
                  "Lifetime access - no recurring fees"
                ] : [
                  "Lifetime Access – $97 once, no recurring fees",
                  "Clarity with my proven 90-day goal framework",
                  "Custom daily action plan—no guesswork",
                  "Productivity synced to your natural rhythm",
                  "Built-in accountability to your biggest goals"
                ])).map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#87A6A7]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-[#87A6A7]" />
                    </div>
                    <span className="text-[#726158] text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {isMonthlyDirect && (
                <p className="text-[#AA9283] text-xs leading-relaxed mt-5 pt-5 border-t border-[#EAD8C2]/60" data-testid="monthly-billing-terms">
                  One-time payment. No subscription. Yours for good.<a href="mailto:hello@theambitionplanner.com" className="underline">hello@theambitionplanner.com</a> — no calls, no hoops.
                </p>
              )}

              {isGlowUpTrial && (
                <p className="text-[#AA9283] text-xs leading-relaxed mt-5 pt-5 border-t border-[#EAD8C2]/60" data-testid="glow-up-billing-terms">
                  Your $9 starts your 7 days with Dea. Nothing is charged automatically when your trial ends — if you want to keep her, you can unlock lifetime access yourself for ${pricing.upgrade_price} (your $9 applies). </p>
              )}
            </div>

            {/* Testimonial */}
            <div className="bg-[#EAD8C2]/30 rounded-xl p-4 sm:p-5 border border-[#EAD8C2]">
              <p className="text-[#726158] italic mb-3 text-sm sm:text-base">
                "This is the first time I've stuck with my plan for more than a week. This is the power of daily directions."
              </p>
              <p className="text-[#87A6A7] font-medium text-sm">— Selina J.</p>
            </div>
          </motion.div>

          {/* Payment Section - Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="glass-card rounded-2xl p-5 sm:p-6 sticky top-24">
              <h3 className="font-serif text-lg text-[#726158] mb-4">Order Summary</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#AA9283] text-sm">
                  <span>{isMonthlyDirect ? 'Dea Lifetime Access' : isGlowUpTrial ? 'Dea 7-Day Trial' : isTrial ? 'Dea — 7-Day Trial' : isUpgrade ? 'Lifetime Access Upgrade' : 'Dea Lifetime Access'}</span>
                  <span>${package_?.price || (isTrial || isGlowUpTrial ? 9 : isUpgrade ? pricing.upgrade_price : pricing.lifetime_price)}.00</span>
                </div>
                {isMonthlyDirect && (
                  <div className="flex justify-between text-[#AA9283] text-xs">
                    <span>One-time payment · no subscription</span>
                    <span></span>
                  </div>
                )}
                {isGlowUpTrial && (
                  <div className="flex justify-between text-[#AA9283] text-xs">
                    <span>No automatic charge after your trial — unlock lifetime access only if you choose to</span>
                    <span></span>
                  </div>
                )}
                {isUpgrade && (
                  <div className="flex justify-between text-[#87A6A7] text-xs">
                    <span>7-Day Power Start credit applied</span>
                    <span>-$9.00</span>
                  </div>
                )}
                <div className="border-t border-[#EAD8C2] pt-3 flex justify-between text-[#726158] font-medium">
                  <span>{isMonthlyDirect || isGlowUpTrial ? 'Total due today' : 'Total'}</span>
                  <span className="font-serif text-xl">${package_?.price || (isTrial || isGlowUpTrial ? 9 : isUpgrade ? pricing.upgrade_price : pricing.lifetime_price)}.00</span>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {/* Inline gate: monthly_direct AND glow-up-trial (when no URL params) need
                  email + first_name before PaymentIntent can be created. */}
              {(isMonthlyDirect && !monthlyDetailsLocked && !clientSecret) ||
               (isGlowUpTrial && !glowUpEmail && !monthlyDetailsLocked && !clientSecret) ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!monthlyEmail || !monthlyFirstName) return;
                    setMonthlyDetailsLocked(true);
                  }}
                  className="mb-4 space-y-3"
                  data-testid="monthly-direct-gate-form"
                >
                  <div>
                    <label className="block text-xs text-[#AA9283] mb-1">First name</label>
                    <input
                      type="text"
                      required
                      autoComplete="given-name"
                      value={monthlyFirstName}
                      onChange={(e) => setMonthlyFirstName(e.target.value)}
                      className="w-full rounded-lg border border-[#EAD8C2] bg-white px-3 py-2 text-sm text-[#3D3229] focus:border-[#B8956A] focus:outline-none"
                      placeholder="Ambitious"
                      data-testid="monthly-direct-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[#AA9283] mb-1">Email</label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={monthlyEmail}
                      onChange={(e) => setMonthlyEmail(e.target.value)}
                      className="w-full rounded-lg border border-[#EAD8C2] bg-white px-3 py-2 text-sm text-[#3D3229] focus:border-[#B8956A] focus:outline-none"
                      placeholder="you@example.com"
                      data-testid="monthly-direct-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={!monthlyEmail || !monthlyFirstName}
                    className="w-full btn-gold py-5 text-sm sm:text-base"
                    data-testid="monthly-direct-continue-btn"
                  >
                    {isGlowUpTrial ? 'Continue to Payment →' : 'Continue to Payment →'}
                  </Button>
                  <p className="text-xs text-[#AA9283] text-center">
                    {isGlowUpTrial ? (
                      <>You{"\u2019"}ll be charged <strong>$9 today</strong> for your 7-day trial. Nothing is charged automatically after that — ever.</>
                    ) : (
                      <>One-time payment of <strong>${pricing.lifetime_price}</strong>. No subscription. Yours for good.</>
                    )}
                  </p>
                </form>
              ) : stripePromise && clientSecret ? (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#CAAB84',
                        colorBackground: '#FFFCF7',
                        colorText: '#726158',
                        colorDanger: '#df1b41',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        spacingUnit: '4px',
                        borderRadius: '8px',
                      },
                    },
                  }}
                >
                  <EmbeddedPaymentForm
                    returnUrl={`${window.location.origin}/checkout/success${isGlowUpTrial ? `?source=glow-up-gap${glowUpReportId ? `&report_id=${encodeURIComponent(glowUpReportId)}` : ''}` : isMonthlyDirect ? '?source=monthly-direct' : ''}`}
                    ctaLabel={isMonthlyDirect ? `Become a Member · $37/mo` : isGlowUpTrial ? `Start My 7-Day Trial →` : isTrial ? `Start My 7-Day Trial · $9` : isUpgrade ? `Complete Upgrade · $${package_?.price || pricing.upgrade_price}` : `Get Lifetime Access · $${pricing.lifetime_price}`}
                    disabled={paypalLoading}
                    defaultEmail={user?.email || monthlyEmail || glowUpEmail || ''}
                    finalizeSubscription={isGlowUpTrial || isMonthlyDirect}
                  />
                </Elements>
              ) : (
                <div className="mb-3 py-8 flex items-center justify-center text-[#AA9283]" data-testid="payment-element-loading">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  <span className="text-sm">{intentLoading ? 'Securing your checkout…' : 'Loading payment form…'}</span>
                </div>
              )}

              {/* Accepted Card Methods */}
              <div className="mb-4 mt-3">
                <PaymentIcons />
              </div>

              {/* PayPal option — hidden for trial/subscription flows (Stripe-only for recurring billing) */}
              {!isTrial && !isGlowUpTrial && !isMonthlyDirect && (
                <>
                  {/* Divider */}
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 border-t border-[#EAD8C2]"></div>
                    <span className="text-[#AA9283] text-xs">or</span>
                    <div className="flex-1 border-t border-[#EAD8C2]"></div>
                  </div>

                  {/* PayPal Button */}
                  <div className="mb-4">
                    {PAYPAL_CLIENT_ID ? (
                      <PayPalScriptProvider options={{
                        clientId: PAYPAL_CLIENT_ID,
                        currency: "USD"
                      }}>
                        <Button
                          onClick={createPayPalOrder}
                          disabled={paypalLoading}
                          className="w-full bg-[#FFC439] hover:bg-[#F0B90B] text-[#003087] py-5 sm:py-6 text-base sm:text-lg font-bold"
                          data-testid="paypal-button"
                        >
                          {paypalLoading ? (
                            <>
                              <Loader2 className="h-5 w-5 animate-spin mr-2" />
                              Connecting to PayPal...
                            </>
                          ) : (
                            <>
                              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.779.779 0 0 1 .77-.654h7.32c2.596 0 4.405.628 5.374 1.867.906 1.161 1.155 2.63.74 4.37l-.018.073c-.633 2.867-2.122 4.86-4.426 5.927-1.403.65-3.126.978-5.126.978H7.522L7.076 21.337z"/>
                              </svg>
                              Pay with PayPal
                            </>
                          )}
                        </Button>
                      </PayPalScriptProvider>
                    ) : (
                      <Button
                        disabled
                        className="w-full bg-gray-200 text-gray-500 py-5 sm:py-6 text-base sm:text-lg"
                      >
                        PayPal Unavailable
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* Security Badges */}
              <div className="flex items-center justify-center gap-4 text-[#B1897C]">
                <div className="flex items-center gap-1 text-xs">
                  <Shield className="h-3 w-3" />
                  <span>SSL Secure</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  </svg>
                  <span>Protected</span>
                </div>
              </div>

              {/* Money Back Guarantee */}
              <div className="mt-4 p-3 rounded-lg bg-[#87A6A7]/10 border border-[#87A6A7]/20 text-center">
                {isMonthlyDirect ? (
                  <>
                    <p className="text-[#726158] text-sm font-medium">Cancel Anytime · No Contracts</p>
                    <p className="text-[#AA9283] text-xs mt-1">Email <a href="mailto:hello@theambitionplanner.com" className="underline">hello@theambitionplanner.com</a> to cancel — no hoops, no hold time.</p>
                  </>
                ) : isTrial ? (
                  <>
                    <p className="text-[#726158] text-sm font-medium">No Auto-Renewal · No Surprise Charges</p>
                    <p className="text-[#AA9283] text-xs mt-1">After 7 days, choose to upgrade or walk away. Your $9 always applies toward lifetime access.</p>
                  </>
                ) : (
                  <>
                    <p className="text-[#726158] text-sm font-medium">30-Day Money Back Guarantee</p>
                    <p className="text-[#AA9283] text-xs mt-1">Not satisfied? Get a full refund, no questions asked.</p>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#EAD8C2] py-6 bg-[#FFFCF7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-[#B1897C] text-sm">
            © 2026 The Ambition Brand. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
    </>
  );
}
